import React, { useEffect, useState } from "react";
import {
  Container,
  Table,
  Spinner,
  Button,
  Modal,
  Form,
  InputGroup,
  Alert,
  Row,
  Col,
  Card,
  Badge,
  Nav,
  Pagination,
  Dropdown,
} from "react-bootstrap";
import axios from "axios";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Search, X } from "react-bootstrap-icons";
import { FaDownload } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { getActionPermissions } from "../../Utils/ActionPermissionChecker";
import { showMyAlert } from "../../Utils/alertService";
import { doAuditLog } from "../../Utils/saveHistoryAction";

const SetSalary = () => {
  const userRole = sessionStorage.getItem("role");
  const { canView, canAdd, canEdit, canDelete } =
    getActionPermissions(userRole);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState("salary"); // "salary" or "overtime"

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // Separate form data for salary and overtime
  const [salaryFormData, setSalaryFormData] = useState({
    salaryType: "hourly",
    amount: "",
    effectiveDate: new Date().toISOString().split("T")[0],
  });

  const [overtimeFormData, setOvertimeFormData] = useState({
    amount: "",
    effectiveDate: new Date().toISOString().split("T")[0],
  });

  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    location: "",
    shift: "",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState("");
  const navigate = useNavigate();

  const companyKey = sessionStorage.getItem("companyKey");

  useEffect(() => {
    const fetchCurrentUserRole = async () => {
      try {
        const userMobile =
          localStorage.getItem("userMobile") ||
          sessionStorage.getItem("userMobile");
        if (!userMobile) {
          navigate("/signIn");
          return;
        }

        const response = await fetch(
          "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollCreateAdmin_PayrollAdminList"
        );
        if (!response.ok) {
          throw new Error("Failed to fetch admin data");
        }

        const data = await response.json();
        // Handle both array and single object responses
        const adminList = Array.isArray(data) ? data : [data];

        // Find current user in admin list
        const currentUser = adminList.find(
          (admin) => admin.mobileNumber === userMobile
        );
        if (currentUser) {
          setCurrentUserRole(currentUser.role);
          // Store role in session storage for easy access
          sessionStorage.setItem("role", currentUser.role);
        } else {
          // If not found in admin list, assume employee role
          setCurrentUserRole("employee");
          sessionStorage.setItem("role", "employee");
        }
      } catch (error) {
        console.error("Error fetching admin role:", error);
        // Default to employee role if error occurs
        setCurrentUserRole("employee");
        sessionStorage.setItem("role", "employee");
      }
    };

    fetchCurrentUserRole();
  }, [navigate]);

  // Check if current user is superadmin
  const isSuperAdmin = currentUserRole === "superadmin";

  useEffect(() => {
    const checkAuth = () => {
      const userId = sessionStorage.getItem("userMobile");
      if (!userId) {
        navigate("/signIn");
      }
    };

    checkAuth();
  }, [navigate]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers"
        );

        // Filter users with approval_status: true and parse their data
        const filteredData = response.data.filter(
          (d) => d.companyKey === companyKey
        );
        const approvedUsers = filteredData
          .filter((user) => user.approval_status === "true")
          .map((user) => ({
            ...user,
            shift: user.shift
              ? typeof user.shift === "string"
                ? JSON.parse(user.shift)
                : user.shift
              : [],
            salary: user.salary
              ? typeof user.salary === "string"
                ? JSON.parse(user.salary)
                : user.salary
              : [],
            overTimeSalary: user.overTimeSalary
              ? typeof user.overTimeSalary === "string"
                ? JSON.parse(user.overTimeSalary)
                : user.overTimeSalary
              : [],
          }))
          .reverse(); // Keep the reverse here

        setUsers(approvedUsers);
        setFilteredUsers(approvedUsers);
      } catch (err) {
        console.error("Error fetching users:", err);
        showMyAlert({
          type: "danger", // "success" | "danger" | "warning" | "info"
          title: "Error",
          message: "Failed to fetch users. Please try again.",
          duration: 2500,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
    setTotalPages(totalPages);

    // Reset to page 1 if current page exceeds total pages after items per page change
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [filteredUsers, itemsPerPage, currentPage]);

  // Apply filters whenever filters or search query changes
  useEffect(() => {
    let result = [...users];

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (user) =>
          user.full_name?.toLowerCase().includes(query) ||
          user.employee_id?.toLowerCase().includes(query) ||
          user.mobile_number?.includes(query) ||
          user.email?.toLowerCase().includes(query) ||
          user.locationName?.toLowerCase().includes(query) ||
          (user.shift &&
            user.shift.some((s) => s.shiftName?.toLowerCase().includes(query)))
      );
    }

    // Apply location filter
    if (filters.location) {
      result = result.filter(
        (user) =>
          user.locationName?.toLowerCase() === filters.location.toLowerCase()
      );
    }

    // Apply shift filter
    if (filters.shift) {
      result = result.filter(
        (user) =>
          user.shift && user.shift.some((s) => s.shiftName === filters.shift)
      );
    }

    setFilteredUsers(result);
  }, [users, searchQuery, filters]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentRecords = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (count) => {
    setItemsPerPage(count);
    setCurrentPage(1); // Reset to first page when items per page changes
  };

  // Get all unique locations for filter dropdown
  const getLocations = () => {
    const locations = new Set();
    users.forEach((user) => {
      if (user.locationName) {
        locations.add(user.locationName);
      }
    });
    return Array.from(locations);
  };

  // Get all unique shifts for filter dropdown
  const getShifts = () => {
    const shifts = new Set();
    users.forEach((user) => {
      if (user.shift) {
        user.shift.forEach((s) => {
          if (s.shiftName) {
            shifts.add(s.shiftName);
          }
        });
      }
    });
    return Array.from(shifts);
  };

  // Get latest record based on dateAdded
  const getLatestRecord = (records) => {
    if (!records || records.length === 0) return null;
    return records.sort(
      (a, b) => new Date(b.dateAdded) - new Date(a.dateAdded)
    )[0];
  };

  const handleSetSalary = (user, type = "salary") => {
    setSelectedUser(user);
    setActiveTab(type);

    // Get latest salary and overtime salary based on dateAdded
    const latestSalary = getLatestRecord(user.salary);
    const latestOvertime = getLatestRecord(user.overTimeSalary);

    setSalaryFormData({
      salaryType: latestSalary?.salaryType || "hourly",
      amount: latestSalary?.amount || "",
      effectiveDate: new Date().toISOString().split("T")[0],
    });

    setOvertimeFormData({
      amount: latestOvertime?.amount || "",
      effectiveDate: new Date().toISOString().split("T")[0],
    });

    setShowModal(true);
  };

  const handleSalaryInputChange = (e) => {
    const { name, value } = e.target;
    setSalaryFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOvertimeInputChange = (e) => {
    const { name, value } = e.target;
    setOvertimeFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetFilters = () => {
    setFilters({
      location: "",
      shift: "",
    });
    setSearchQuery("");
  };

  const handleSalarySubmit = async () => {
    try {
      if (!salaryFormData.amount || !salaryFormData.effectiveDate) {
        showMyAlert({
          type: "info", // "success" | "danger" | "warning" | "info"
          title: "Info",
          message: "Please fill all required data",
          duration: 2500, // auto-close in 2.5s
        });
        return;
      }

      setSubmitting(true);

      const currentUserResponse = await axios.get(
        `https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers/${selectedUser.id}`
      );
      const currentUser = currentUserResponse.data;

      const oldSalaryData = currentUser.salary
        ? typeof currentUser.salary === "string"
          ? JSON.parse(currentUser.salary)
          : currentUser.salary
        : [];

      const salaryRecord = {
        id: Date.now(),
        salaryType: salaryFormData.salaryType,
        amount: salaryFormData.amount,
        shift:
          selectedUser.shift && selectedUser.shift.length > 0
            ? selectedUser.shift[0].shiftName
            : "",
        effectiveDate: salaryFormData.effectiveDate,
        dateAdded: new Date().toISOString(),
      };

      // Create new salary array with new record at the beginning
      const newSalaryData = [salaryRecord, ...oldSalaryData];

      const updatedUser = {
        ...currentUser,
        salary: newSalaryData,
      };

      await axios.put(
        `https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers/${selectedUser.id}`,
        updatedUser
      );

      doAuditLog("Update", "Set Salary", selectedUser, updatedUser);

      showMyAlert({
        type: "success", // "success" | "danger" | "warning" | "info"
        title: "Updated",
        message: `Salary updated successfully`,
        duration: 2500, // auto-close in 2.5s
      });
      await refreshUserList();
      setShowModal(false);
    } catch (error) {
      console.error("Error updating salary:", error);
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Failed",
        message: "Failed to updated salary, please try again later",
        duration: 2500, // auto-close in 2.5s
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleOvertimeSubmit = async () => {
    try {
      if (!overtimeFormData.amount || !overtimeFormData.effectiveDate) {
        showMyAlert({
          type: "info", // "success" | "danger" | "warning" | "info"
          title: "Info",
          message: "Please fill all required fields",
          duration: 2500, // auto-close in 2.5s
        });
        return;
      }

      setSubmitting(true);

      // Get the current state of the user before making changes
      const currentUserResponse = await axios.get(
        `https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers/${selectedUser.id}`
      );
      const currentUser = currentUserResponse.data;

      // Parse the existing overtime data if it's a string
      const oldOvertimeData = currentUser.overTimeSalary
        ? typeof currentUser.overTimeSalary === "string"
          ? JSON.parse(currentUser.overTimeSalary)
          : currentUser.overTimeSalary
        : [];

      // Prepare overtime salary data
      const overtimeRecord = {
        id: Date.now(),
        salaryType: "hourly",
        amount: overtimeFormData.amount,
        shift:
          selectedUser.shift && selectedUser.shift.length > 0
            ? selectedUser.shift[0].shiftName
            : "",
        effectiveDate: overtimeFormData.effectiveDate,
        dateAdded: new Date().toISOString(),
      };

      const newOvertimeData = [overtimeRecord, ...oldOvertimeData];

      // Update user data with only overtime changes
      const updatedUser = {
        ...currentUser,
        overTimeSalary: newOvertimeData,
      };

      // Send update to API
      await axios.put(
        `https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers/${selectedUser.id}`,
        updatedUser
      );

      doAuditLog("Update", "Set Salary", selectedUser, updatedUser);

      showMyAlert({
        type: "success", // "success" | "danger" | "warning" | "info"
        title: "Updated",
        message: "Overtime salary Updated successfully",
        duration: 2500, // auto-close in 2.5s
      });

      // Refresh user list
      await refreshUserList();
      setShowModal(false);
    } catch (error) {
      console.error("Error updating overtime salary:", error);
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Failed",
        message: "Failed to update overtime salary. Please try again.",
        duration: 2500, // auto-close in 2.5s
      });
    } finally {
      setSubmitting(false);
    }
  };

  const refreshUserList = async () => {
    const response = await axios.get(
      "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers"
    );

    const filteredData = response.data.filter(
      (d) => d.companyKey === companyKey
    );

    const updatedUsers = filteredData
      .filter((user) => user.approval_status === "true")
      .map((user) => ({
        ...user,
        shift: user.shift
          ? typeof user.shift === "string"
            ? JSON.parse(user.shift)
            : user.shift
          : [],
        salary: user.salary
          ? typeof user.salary === "string"
            ? JSON.parse(user.salary)
            : user.salary
          : [],
        overTimeSalary: user.overTimeSalary
          ? typeof user.overTimeSalary === "string"
            ? JSON.parse(user.overTimeSalary)
            : user.overTimeSalary
          : [],
      }))
      .reverse(); // Add reverse here to maintain consistent ordering

    setUsers(updatedUsers);
    setFilteredUsers(updatedUsers);
  };

  const getCurrentSalary = (user) => {
    if (!user.salary || user.salary.length === 0) return "Not set";

    const latest = getLatestRecord(user.salary);
    if (!latest) return "Not set";

    return latest.salaryType === "hourly"
      ? `₹${latest.amount}/hour`
      : `₹${latest.amount}/month`;
  };

  const getCurrentOvertime = (user) => {
    if (!user.overTimeSalary || user.overTimeSalary.length === 0)
      return "Not set";

    const latest = getLatestRecord(user.overTimeSalary);
    if (!latest) return "Not set";

    return `₹${latest.amount}/hour`;
  };

  const formatShiftTime = (shift) => {
    if (!shift || !shift.startTime || !shift.endTime) return "N/A";
    return `${shift.startTime} - ${shift.endTime}`;
  };

  const isMobile = window.innerWidth < 768;

  const handleDownloadPDF = () => {
    const tableColumn = [
      "#",
      "Name",
      "Employee ID",
      "Mobile",
      "Email",
      "Location",
      "Shift",
      "Current Salary",
      "Overtime Rate",
    ];
    const tableRows = currentRecords.map((user, index) => [
      indexOfFirstItem + index + 1,
      user.full_name || "N/A",
      user.employee_id || "N/A",
      user.mobile_number || "N/A",
      user.email || "N/A",
      user.locationName || "N/A",
      user.shift && user.shift.length > 0 ? user.shift[0].shiftName : "N/A",
      getCurrentSalary(user),
      getCurrentOvertime(user),
    ]);
    if (tableRows.length === 0) {
      showMyAlert({
        type: "info", // "success" | "danger" | "warning" | "info"
        title: "No data",
        message: "No data available to download",
        duration: 2500, // auto-close in 2.5s
      });
      return;
    }
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Employee Salary Management", 14, 16);
    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 22,
      styles: { fontSize: 10, cellPadding: 2 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { left: 14, right: 14 },
    });
    doc.save("Employee_Salary_Management.pdf");
  };

  const handleDownloadExcel = () => {
    if (filteredUsers.length === 0) {
      showMyAlert({
        type: "info", // "success" | "danger" | "warning" | "info"
        title: "No data",
        message: "No data available to download",
        duration: 2500, // auto-close in 2.5s
      });
      return;
    }

    // Prepare Excel data
    const worksheetData = filteredUsers.map((user, index) => ({
      "S.I": index + 1,
      Name: user.full_name || "N/A",
      "Employee ID": user.employee_id || "N/A",
      Mobile: user.mobile_number || "N/A",
      Email: user.email || "N/A",
      Location: user.locationName || "N/A",
      Shift:
        user.shift && user.shift.length > 0 ? user.shift[0].shiftName : "N/A",
      "Shift Time":
        user.shift && user.shift.length > 0
          ? `${user.shift[0].startTime || ""} - ${user.shift[0].endTime || ""}`
          : "N/A",
      "Current Salary": getCurrentSalary(user),
      "Overtime Rate": getCurrentOvertime(user),
    }));

    // Create a worksheet
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);

    // Auto adjust column width
    const colWidths = Object.keys(worksheetData[0]).map(() => ({ wch: 20 }));
    worksheet["!cols"] = colWidths;

    // Create a workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Employee Salary");

    // Write and save file
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    const timestamp = new Date().toISOString().split("T")[0];
    saveAs(blob, `Employee_Salary_Management_${timestamp}.xlsx`);
  };

  return (
    <Container className="mt-3">
      {/* Header Section */}
      <div className="row align-items-center mb-2 border rounded bg-light py-2 mx-2">
        <div className="col-12 col-md-6 mb-2 mb-md-0">
          <h5 className="mb-0 d-flex align-items-center justify-content-center justify-content-md-start gap-2">
            Employee Salary Management
          </h5>
        </div>
        <div className="col-12 col-md-6 justify-content-center justify-content-md-end text-md-end">
          <div className="d-flex justify-content-center justify-content-md-end gap-2">
            <Button
              variant="outline-success"
              size="sm"
              title="Download Excel"
              onClick={handleDownloadExcel}
            >
              Export Excel
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              title="Download PDF"
              onClick={handleDownloadPDF}
            >
              Export PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Search Section */}
      <div className="row align-items-center mb-3 border rounded bg-light py-2 mx-1">
        <div className="col-12 col-md-5 mb-2 mb-md-0">
          <label className="form-label small text-muted mb-1">Search</label>
          <div className="input-group input-group-sm">
            <span className="input-group-text bg-light border-end-0">
              <Search size={16} className="text-muted" />
            </span>
            <input
              type="text"
              className="form-control border-start-0"
              placeholder="Search here..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="col-12 col-md-7 mt-2 mt-md-0">
          <div className="row g-2 align-items-end justify-content-start">
            {/* Location Filter */}
            <div className="col-6 col-md-4">
              <label className="form-label small text-muted mb-1">
                Location
              </label>
              <Form.Select
                size="sm"
                name="location"
                value={filters.location}
                onChange={handleFilterChange}
              >
                <option value="">All Locations</option>
                {getLocations().map((location, index) => (
                  <option key={index} value={location}>
                    {location}
                  </option>
                ))}
              </Form.Select>
            </div>

            {/* Shift Filter */}
            <div className="col-6 col-md-4">
              <label className="form-label small text-muted mb-1">Shift</label>
              <Form.Select
                size="sm"
                name="shift"
                value={filters.shift}
                onChange={handleFilterChange}
              >
                <option value="">All Shifts</option>
                {getShifts().map((shift, index) => (
                  <option key={index} value={shift}>
                    {shift}
                  </option>
                ))}
              </Form.Select>
            </div>

            {(searchQuery || filters.location || filters.shift) && (
              <div className="col-12 col-md-4 d-flex align-items-end">
                <Button
                  variant="outline-danger"
                  size="sm"
                  className=""
                  onClick={() => {
                    setSearchQuery("");
                    resetFilters();
                  }}
                >
                  Clear
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pagination Section */}
      {filteredUsers.length > 0 && (
        <div className="row align-items-center justify-content-between mb-2 p-2">
          {/* Items per page selector */}
          <div className="col-5 col-md-auto">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">Paginate</span>
              <Dropdown>
                <Dropdown.Toggle
                  size="sm"
                  variant="outline-secondary"
                  id="items-per-page-dropdown"
                >
                  {itemsPerPage}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {[5, 10, 20, 50, 100].map((count) => (
                    <Dropdown.Item
                      key={count}
                      onClick={() => handleItemsPerPageChange(count)}
                      active={itemsPerPage === count}
                    >
                      {count}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>

          {/* Pagination controls */}
          <div className="col-7 col-md-auto">
            <Pagination
              size="sm"
              className="mb-0 justify-content-center justify-content-md-end"
            >
              <Pagination.First
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />

              {/* Mobile: Show simplified pagination */}
              {isMobile ? (
                <>
                  <Pagination.Item active>{currentPage}</Pagination.Item>
                  {totalPages > 1 && (
                    <>
                      <Pagination.Ellipsis disabled />
                      <Pagination.Item
                        onClick={() => handlePageChange(totalPages)}
                      >
                        {totalPages}
                      </Pagination.Item>
                    </>
                  )}
                </>
              ) : (
                /* Desktop: Show full pagination */
                Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <Pagination.Item
                      key={page}
                      active={page === currentPage}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </Pagination.Item>
                  )
                )
              )}

              <Pagination.Next
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
              <Pagination.Last
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="mt-2">Loading employees...</p>
        </div>
      ) : filteredUsers.length === 0 ? (
        <Alert variant="info" className="text-center">
          No employees found matching your criteria
        </Alert>
      ) : isMobile ? (
        <div>
          {currentRecords.map((user, index) => (
            <div key={user.id} className="mb-3">
              <div
                className="card shadow-sm"
                style={{ textAlign: "left", borderRadius: "12px" }}
              >
                <div className="card-body table-responsive">
                  <table className="table table-striped table-bordered mb-2">
                    <tbody>
                      <tr>
                        <td className="fw-bold">SI</td>
                        <td>{filteredUsers.length - index}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Name</td>
                        <td>{user.full_name}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Employee ID</td>
                        <td>{user.employee_id}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Mobile</td>
                        <td>{user.mobile_number}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Email</td>
                        <td>{user.email || "N/A"}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Location</td>
                        <td>{user.locationName || "N/A"}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Shift</td>
                        <td>
                          {user.shift && user.shift.length > 0 ? (
                            <>
                              <div>{user.shift[0].shiftName}</div>
                              <div className="text-muted small">
                                {formatShiftTime(user.shift[0])}
                              </div>
                            </>
                          ) : (
                            "N/A"
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Current Salary</td>
                        <td>{getCurrentSalary(user)}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Overtime Rate</td>
                        <td>{getCurrentOvertime(user)}</td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="d-flex justify-content-end align-itmes-center gap-2 flex-wrap">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2 mb-1"
                      onClick={() => handleSetSalary(user, "salary")}
                    >
                      Set Salary
                    </Button>
                    <Button
                      variant="outline-info"
                      size="sm"
                      className="mb-1"
                      onClick={() => handleSetSalary(user, "overtime")}
                    >
                      Set Overtime
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="table-responsive">
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th id="text-white-important">S.I</th>
                <th id="text-white-important">Employee Details</th>
                <th id="text-white-important">Contact</th>
                <th id="text-white-important">Email</th>
                <th id="text-white-important">Location</th>
                <th id="text-white-important">Shift</th>
                <th id="text-white-important">Current Salary</th>
                <th id="text-white-important">Overtime Rate</th>
                <th id="text-white-important">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentRecords.map((user, index) => (
                <tr key={user.id}>
                  <td>{filteredUsers.length - index}</td>
                  <td>
                    <div className="fw-bold">{user.full_name}</div>
                    <div className="text-muted small">
                      ID: {user.employee_id}
                    </div>
                  </td>
                  <td>{user.mobile_number}</td>
                  <td>{user.email || "N/A"}</td>
                  <td>{user.locationName || "N/A"}</td>
                  <td>
                    {user.shift && user.shift.length > 0 ? (
                      <>
                        <div>{user.shift[0].shiftName}</div>
                        <div className="text-muted small">
                          {formatShiftTime(user.shift[0])}
                        </div>
                      </>
                    ) : (
                      "N/A"
                    )}
                  </td>
                  <td>{getCurrentSalary(user)}</td>
                  <td>{getCurrentOvertime(user)}</td>
                  <td
                    className="align-middle"
                    style={{ verticalAlign: "middle", width: "200px" }}
                  >
                    <div className="d-flex flex-wrap align-items-center justify-content-start gap-1 h-100">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleSetSalary(user, "salary")}
                      >
                        Set Salary
                      </Button>
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() => handleSetSalary(user, "overtime")}
                      >
                        Set Overtime
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* Showing entries info */}
      <div className="col-12  mb-2">
        <span className="text-muted">
          Showing {indexOfFirstItem + 1} to{" "}
          {Math.min(indexOfLastItem, filteredUsers.length)} of{" "}
          {filteredUsers.length} entries
        </span>
      </div>

      {/* Salary Setting Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Set {activeTab === "salary" ? "Salary" : "Overtime Rate"} for{" "}
            {selectedUser?.full_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activeTab === "salary" ? (
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Salary Type *</Form.Label>
                    <Form.Select
                      name="salaryType"
                      value={salaryFormData.salaryType}
                      onChange={handleSalaryInputChange}
                    >
                      <option value="hourly">Hourly Rate</option>
                      <option value="fixed">Monthly Salary</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {salaryFormData.salaryType === "hourly"
                        ? "Hourly Rate *"
                        : "Monthly Salary *"}
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text>₹</InputGroup.Text>
                      <Form.Control
                        type="number"
                        name="amount"
                        value={salaryFormData.amount}
                        onChange={handleSalaryInputChange}
                        placeholder={
                          salaryFormData.salaryType === "hourly"
                            ? "e.g. 150"
                            : "e.g. 25000"
                        }
                        required
                      />
                      <InputGroup.Text>
                        {salaryFormData.salaryType === "hourly"
                          ? "/hour"
                          : "/month"}
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Effective Date *</Form.Label>
                    <Form.Control
                      type="date"
                      name="effectiveDate"
                      value={salaryFormData.effectiveDate}
                      onChange={handleSalaryInputChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          ) : (
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Overtime Rate (Hourly) *</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>₹</InputGroup.Text>
                      <Form.Control
                        type="number"
                        name="amount"
                        value={overtimeFormData.amount}
                        onChange={handleOvertimeInputChange}
                        placeholder="e.g. 200"
                        required
                      />
                      <InputGroup.Text>/hour</InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Effective Date *</Form.Label>
                    <Form.Control
                      type="date"
                      name="effectiveDate"
                      value={overtimeFormData.effectiveDate}
                      onChange={handleOvertimeInputChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          )}

          {selectedUser?.shift && selectedUser.shift.length > 0 && (
            <Alert variant="info" className="mt-3">
              <strong>Shift Information:</strong>{" "}
              {selectedUser.shift[0].shiftName} (
              {formatShiftTime(selectedUser.shift[0])})
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          {activeTab === "salary" ? (
            <Button
              variant="primary"
              onClick={handleSalarySubmit}
              disabled={
                submitting ||
                !salaryFormData.amount ||
                !salaryFormData.effectiveDate
              }
            >
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                "Save Salary"
              )}
            </Button>
          ) : (
            <Button
              variant="success"
              onClick={handleOvertimeSubmit}
              disabled={
                submitting ||
                !overtimeFormData.amount ||
                !overtimeFormData.effectiveDate
              }
            >
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                "Save Overtime Rate"
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default SetSalary;
